/**
 * The in-shell states a caller reaches when there is nothing for them to open
 * (f9.2). Each is a different situation with a different remedy, so each has
 * its own heading -- merging them was the easy mistake available here:
 *
 * | State | Situation |
 * | --- | --- |
 * | `No access yet` | an active role, and no part of the hierarchy names them |
 * | `Limited access` | named on individual engagements only, whose client is not theirs (Q-f9.2-2) |
 * | `Not available to you` | a deep link to a screen the server did not send |
 *
 * f1.3's `Unassigned` and `deactivated` screens are out of the shell on
 * `SignIn` and are not edited here: *no role has been assigned* is not *your
 * role grants you no part of the hierarchy*.
 *
 * Copy only. No state here decides anything: the server already did, and
 * every endpoint behind every hidden entry still refuses on its own authority.
 * No copy names a node, a count of anything below a root, or another person.
 */

import { Link } from 'react-router-dom';
import { Compass, KeyRound, ShieldOff } from 'lucide-react';
import type { ReactNode } from 'react';

import { recoveryPathFor } from '@/navigation/landing';
import { useSessionScope } from '@/session/SessionScopeContext';

function StatePage({
  title,
  sub,
  icon,
  heading,
  children,
}: {
  title: string;
  sub: string;
  icon: ReactNode;
  heading: string;
  children: ReactNode;
}) {
  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{title}</h1>
          <div className="page-sub">{sub}</div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="empty">
            <div className="e-icon">{icon}</div>
            <h3>{heading}</h3>
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

/** Active, a real role, and named nowhere: the empty application, made visible. */
export function NoAccessYet() {
  return (
    <StatePage
      title="Welcome to Atlas"
      sub="You are signed in"
      icon={<KeyRound aria-hidden="true" />}
      heading="No access yet"
    >
      <p>
        Your account is active, but no part of the GFT US hierarchy names you as an
        owner, so there is nothing for you to see yet.
      </p>
      <p>
        An administrator can name you on a Business Unit Group, Business Unit, Client or
        Engagement, or the next master workbook import will put you in scope.
      </p>
    </StatePage>
  );
}

/**
 * Named on engagements only (Q-f9.2-2). NOT "you are named nowhere": they are,
 * and the audit log would say so. `scope` carries counts and never ids, so no
 * engagement is listed.
 */
export function LimitedAccess() {
  return (
    <StatePage
      title="Welcome to Atlas"
      sub="You are signed in"
      icon={<Compass aria-hidden="true" />}
      heading="Limited access"
    >
      <p>
        You are named on individual engagements. In Atlas an engagement is opened from
        its client, and the client is not part of your scope, so there is no screen to
        open yet.
      </p>
      <p>If you need to work on these engagements here, ask an administrator.</p>
    </StatePage>
  );
}

/**
 * A deep link to a screen the server did not include in `navigation`.
 *
 * Rendered rather than redirected, so the address stays as typed and cannot
 * loop against the computed landing. Presentation only: had it rendered the
 * screen instead, every request on it would have been refused or narrowed by
 * the server.
 */
export function NotAvailableToYou() {
  const { session } = useSessionScope();
  return (
    <StatePage
      title="Not available"
      sub="This area is not part of your Atlas menu"
      icon={<ShieldOff aria-hidden="true" />}
      heading="Not available to you"
    >
      <p>There is nothing in this area of Atlas for your account.</p>
      <p className="empty-actions">
        <Link className="btn btn-primary" to={recoveryPathFor(session?.navigation ?? [])}>
          Go to your start page
        </Link>
      </p>
    </StatePage>
  );
}
